import retry from 'async-retry';

/**
 * Wrapper class for the hls.js library.
 *
 * Documentation here:
 *  https://github.com/video-dev/hls.js/blob/master/docs/API.md
 */
export default class HlsJs {
  constructor(fetchHlsJsLibrary) {
    this.fetchHlsJsLibrary = fetchHlsJsLibrary;
    this.hlsJsInstance = null;
    this.Events = {};
    this.listeners = [];
  }

  async init() {
    const HlsJSLibrary = await retry(this.fetchHlsJsLibrary, { retries: 3 });
    if (!HlsJSLibrary) {
      throw new Error('could not load hls.js library');
    }
    this.hlsJsInstance = new HlsJSLibrary({
      autoStartLoad: false,
    });
    this.Events = HlsJSLibrary.Events;
  }

  addEventListener(eventName, callback, once = false) {
    const event = this.Events[eventName];

    if (!event) {
      return;
    }

    if (once) {
      this.hlsJsInstance.once(event, callback);
      return;
    }

    this.hlsJsInstance.on(event, callback);
  }

  removeEventListener(eventName, callback) {
    const event = this.Events[eventName];

    if (event) {
      this.hlsJsInstance.off(event, callback);
    }
  }

  /**
   * Method to attach the audioElement to the hls.js library so the
   * HLS manifest segments can be injected into the audio element's source
   * buffer.
   * @param audioElement {HTMLAudioElement}
   * @returns {Promise<any>}
   */
  attachMedia(audioElement) {
    return new Promise((resolve, reject) => {
      this.listeners.push({
        event: this.Events.MEDIA_ATTACHED,
        callback: resolve,
      });
      this.listeners.push({ event: this.Events.ERROR, callback: reject });
      this.hlsJsInstance.once(this.Events.MEDIA_ATTACHED, resolve);
      this.hlsJsInstance.once(this.Events.ERROR, reject);
      this.hlsJsInstance.attachMedia(audioElement);
    });
  }

  /**
   * Method to load the HLS manifest specified by the url, and then start to load
   * the manifest fragments starting at the live head. We are using 0 as the default position
   * for live streams and in cases of VOD (LiveSeek) we use -1 as the default position to get
   * us to the current live head position.
   *
   * Documentation:
   *  https://github.com/video-dev/hls.js/blob/master/docs/API.md#startposition
   *
   * @param url {string} HLS manifest url
   * @param position {number} position to start playing the HLS manifest
   * @param isLiveSeek {boolean} is the loaded source from a Live Seek Player or not
   * @returns {Promise<void>}
   */
  async loadSource(url, position, isLiveSeek) {
    const defaultStartPos = isLiveSeek ? -1 : 0;
    const startingPosition =
      typeof position === 'number' ? position : defaultStartPos;
    await new Promise((resolve, reject) => {
      this.listeners.push({
        event: this.Events.MANIFEST_PARSED,
        callback: resolve,
      });
      this.listeners.push({ event: this.Events.ERROR, callback: reject });
      this.hlsJsInstance.once(this.Events.MANIFEST_PARSED, resolve);
      this.hlsJsInstance.once(this.Events.ERROR, reject);
      this.hlsJsInstance.loadSource(url);
    });
    this.hlsJsInstance.startLoad(startingPosition);
  }

  off() {
    this.listeners.forEach((listener) =>
      this.hlsJsInstance.off(listener.event, listener.callback),
    );
    this.listeners = [];
  }

  startLoad() {
    this.hlsJsInstance.startLoad();
  }

  stopLoad() {
    this.hlsJsInstance.stopLoad();
  }

  detachMedia() {
    this.hlsJsInstance.detachMedia();
  }

  destroy() {
    return this.hlsJsInstance.destroy();
  }
}
